import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  GeoJSON,
  Popup,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* Inline styles for map label overlays */
const LABEL_STYLES = document.createElement("style");
LABEL_STYLES.textContent = `
  .city-label-tooltip {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-weight: 700;
    font-size: 13px;
    color: #1e3a8a;
    text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
    white-space: nowrap;
  }
  .city-label-tooltip::before { display: none !important; }
  .brgy-label-icon span {
    font-size: 13px;
    font-weight: 700;
    color: #1d4ed8;
    text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
    white-space: nowrap;
    pointer-events: none;
    letter-spacing: 0.03em;
  }
`;
if (!document.querySelector("#mig-dash-label-styles")) {
  LABEL_STYLES.id = "mig-dash-label-styles";
  document.head.appendChild(LABEL_STYLES);
}

import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import { MODULE_THEMES } from "../utils/moduleTheme";
import BARANGAY_COORDS, {
  SERVICE_AREA_CENTER,
  SERVICE_AREA_ZOOM,
  CITY_CENTERS,
  getBarangayCoords,
  REFERENCE_BARANGAYS,
} from "../data/barangayGeodata";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MIG = MODULE_THEMES.migration;
const HEAD_OFFICE = [14.611065, 121.089339];
const HEAD_OFFICE_ICON = L.divIcon({
  className: "",
  html: `<div style="
    display:flex;align-items:center;justify-content:center;
    width:32px;height:32px;border-radius:50%;
    background:#1e40af;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
  "><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const STATUS_COLORS = {
  Completed: "#22c55e",
  Cancelled: "#ef4444",
  Rescheduled: "#f97316",
  Pending: "#eab308",
  "Not Yet Visited": "#9ca3af",
};

const WORKFLOW_LABELS = {
  uploaded_for_migration: "Uploaded for Migration",
  for_calling_scheduling: "For Calling/Scheduling",
  for_dispatch: "For Dispatch",
  visited_called: "Completed/Visited/Called",
};

const _TERMINAL_ACTIONS = ["Completed", "Cancelled", "For NI"];

/** Resolve display label for workflow page, accounting for terminal records. */
function resolveWorkflowLabel(rec) {
  if (rec.action_result && _TERMINAL_ACTIONS.includes(rec.action_result)) {
    return WORKFLOW_LABELS.visited_called;
  }
  return WORKFLOW_LABELS[rec.workflow_stage] || rec.workflow_stage;
}

// ---------------------------------------------------------------------------
// Helper: map fly-to component
// ---------------------------------------------------------------------------
function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 15, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

function ZoomWatcher({ onZoomChange }) {
  useMapEvents({
    zoomend(e) {
      onZoomChange(e.target.getZoom());
    },
  });
  return null;
}

// ---------------------------------------------------------------------------
// Helper: barangay name labels rendered at polygon centroids
// ---------------------------------------------------------------------------
function BrgyLabels({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data || !map) return;
    const markers = [];
    (data.features || []).forEach((f) => {
      const name = f.properties?.brgy_name || "";
      if (!name) return;
      const centroid = L.geoJSON(f).getBounds().getCenter();
      const marker = L.marker(centroid, {
        icon: L.divIcon({
          className: "",
          html: `<span style="font-size:13px;font-weight:700;color:#1d4ed8;text-shadow:0 0 4px #fff,0 0 4px #fff,0 0 4px #fff;white-space:nowrap;pointer-events:none;letter-spacing:0.03em;">${name}</span>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
        interactive: false,
      });
      marker.addTo(map);
      markers.push(marker);
    });
    return () => markers.forEach((m) => map.removeLayer(m));
  }, [data, map]);
  return null;
}

// ---------------------------------------------------------------------------
// Helper: determine effective dashboard status for a record
// ---------------------------------------------------------------------------
function effectiveStatus(rec) {
  const s = rec.jo_status;
  if (!s || s === "" || s === "Called" || s === "For NI") return "Not Yet Visited";
  if (["Completed", "Cancelled", "Rescheduled", "Pending"].includes(s)) return s;
  return "Not Yet Visited";
}

// ---------------------------------------------------------------------------
// Helper: dominant status for a barangay (for coloring)
// ---------------------------------------------------------------------------
function dominantStatus(brgy) {
  const statusOrder = [
    ["pending", "Pending"],
    ["not_yet_visited", "Not Yet Visited"],
    ["rescheduled", "Rescheduled"],
    ["cancelled", "Cancelled"],
    ["completed", "Completed"],
  ];
  let best = "Not Yet Visited";
  let bestVal = 0;
  for (const [key, label] of statusOrder) {
    if (brgy[key] > bestVal) {
      bestVal = brgy[key];
      best = label;
    }
  }
  return best;
}

// Team marker colors (cycled for multiple teams)
const TEAM_COLORS = [
  "#e11d48", // rose
  "#7c3aed", // violet
  "#0891b2", // cyan
  "#059669", // emerald
  "#d97706", // amber
  "#dc2626", // red
  "#4f46e5", // indigo
  "#0d9488", // teal
];

// ---------------------------------------------------------------------------
// Helper: circle radius based on total count
// ---------------------------------------------------------------------------
function circleRadius(total) {
  if (total >= 300) return 22;
  if (total >= 100) return 16;
  if (total >= 50) return 13;
  if (total >= 20) return 10;
  return 7;
}

// ---------------------------------------------------------------------------
// Helper: SVG pie-chart icon for barangay markers (multi-status view)
// ---------------------------------------------------------------------------
const STATUS_KEYS = [
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
  ["rescheduled", "Rescheduled"],
  ["pending", "Pending"],
  ["not_yet_visited", "Not Yet Visited"],
];

const pieIconCache = {};

function buildPieIcon(brgy, size) {
  const vals = STATUS_KEYS.map(([k]) => brgy[k] || 0).join(",");
  const cacheKey = `${brgy.municipality}-${brgy.barangay}-${size}-${vals}`;
  if (pieIconCache[cacheKey]) return pieIconCache[cacheKey];

  const slices = [];
  let total = 0;
  for (const [key, label] of STATUS_KEYS) {
    const val = brgy[key] || 0;
    if (val > 0) {
      slices.push({ label, value: val, color: STATUS_COLORS[label] });
      total += val;
    }
  }

  const r = size / 2;
  const cx = r;
  const cy = r;
  let svg;

  if (slices.length <= 1) {
    const color = slices.length === 1 ? slices[0].color : "#9ca3af";
    svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${cx}" cy="${cy}" r="${r - 1}" fill="${color}" fill-opacity="0.8" stroke="#fff" stroke-width="1.5"/>
    </svg>`;
  } else {
    let paths = "";
    let angle = -Math.PI / 2; // start at top
    for (const slice of slices) {
      const sliceAngle = (slice.value / total) * 2 * Math.PI;
      const x1 = cx + (r - 1) * Math.cos(angle);
      const y1 = cy + (r - 1) * Math.sin(angle);
      const x2 = cx + (r - 1) * Math.cos(angle + sliceAngle);
      const y2 = cy + (r - 1) * Math.sin(angle + sliceAngle);
      const largeArc = sliceAngle > Math.PI ? 1 : 0;
      paths += `<path d="M${cx},${cy} L${x1},${y1} A${r - 1},${r - 1} 0 ${largeArc} 1 ${x2},${y2} Z" fill="${slice.color}" fill-opacity="0.8"/>`;
      angle += sliceAngle;
    }
    svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      ${paths}
      <circle cx="${cx}" cy="${cy}" r="${r - 1}" fill="none" stroke="#fff" stroke-width="1.5"/>
    </svg>`;
  }

  const icon = L.divIcon({
    className: "",
    html: svg,
    iconSize: [size, size],
    iconAnchor: [r, r],
  });
  pieIconCache[cacheKey] = icon;
  return icon;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MigrationDashboardPage() {
  const { canAccessMigrationDashboard, hasPerm } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // State
  const [stats, setStats] = useState(null);
  const [brgyData, setBrgyData] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [filterMuni, setFilterMuni] = useState("");
  const [filterBrgy, setFilterBrgy] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBundle, setFilterBundle] = useState("");
  const [bundleTypes, setBundleTypes] = useState([]);
  const [filterDecomm, setFilterDecomm] = useState("");
  const [decommValues, setDecommValues] = useState([]);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const searchTimer = useRef(null);

  // Map focus
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);

  // Barangay records modal
  const [brgyModal, setBrgyModal] = useState(null); // { municipality, barangay }
  const [brgyRecords, setBrgyRecords] = useState([]);
  const [brgyRecordsLoading, setBrgyRecordsLoading] = useState(false);
  const [brgySelected, setBrgySelected] = useState(new Set());
  const [bulkDateModal, setBulkDateModal] = useState(null); // { stage, label }
  const [bulkDate, setBulkDate] = useState("");
  const [bulkJoStatus, setBulkJoStatus] = useState("");
  const [bulkCancelReasons, setBulkCancelReasons] = useState([]);
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const [dispatchTeams, setDispatchTeams] = useState([]);
  const [teamsSummary, setTeamsSummary] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const canTag = hasPerm("migration.tag");
  const canReopenMig = hasPerm("migration.reopen");
  const [confirmReopenId, setConfirmReopenId] = useState(null);
  const [reopenCallRemarks, setReopenCallRemarks] = useState("");

  // Map layers
  const [showBasemap, setShowBasemap] = useState(true);
  const [showSatellite, setShowSatellite] = useState(false);
  const [showStreetLabels, setShowStreetLabels] = useState(true);
  const [showLandmarks, setShowLandmarks] = useState(false);
  const [showBrgyBoundaries, setShowBrgyBoundaries] = useState(false);
  const [showCityBoundaries, setShowCityBoundaries] = useState(true);
  const [showBrgyLabels, setShowBrgyLabels] = useState(true);
  const [brgyGeoJSON, setBrgyGeoJSON] = useState(null);
  const [cityGeoJSON, setCityGeoJSON] = useState(null);

  // Right-side tabbed panel (parity with Migration Dispatch Map)
  const [rightTab, setRightTab] = useState("layers");

  // Build centroid lookup from GeoJSON polygons so circles fall inside boundaries
  const geoCentroids = useMemo(() => {
    const lookup = {};
    if (!brgyGeoJSON) return lookup;
    const CITY_TO_MUNI = {
      "city of pasig": "PASIG",
      "city of mandaluyong": "MANDALUYONG",
      "city of marikina": "MARIKINA",
      "city of san juan": "SAN JUAN",
    };
    // Normalize: strip accents, parentheticals, punctuation, extra spaces
    const norm = (s) =>
      s.normalize("NFD").replace(/[\u0300-\u036f]/g, "")     // remove accents
       .toUpperCase()
       .replace(/\(.*?\)/g, "")                               // remove (Pob.), (Rob.), etc.
       .replace(/[.\-]/g, " ")                                // dots & hyphens → space
       .replace(/\s+/g, " ")
       .trim();

    for (const f of brgyGeoJSON.features || []) {
      const rawBrgy = f.properties?.brgy_name || "";
      const city = (f.properties?.city_name || "").toLowerCase();
      const muni = CITY_TO_MUNI[city];
      if (!muni || !rawBrgy) continue;
      const center = L.geoJSON(f).getBounds().getCenter();
      const coords = [center.lat, center.lng];
      // Store under both raw uppercase and normalized key
      lookup[`${muni}|${rawBrgy.toUpperCase()}`] = coords;
      lookup[`${muni}|${norm(rawBrgy)}`] = coords;
    }

    // Explicit aliases: DB name → GeoJSON name (both stored under MUNI|DB_NAME)
    const ALIASES = {
      "MARIKINA|STA ELENA":          "MARIKINA|SANTA ELENA",
      "MARIKINA|STO NINO":           "MARIKINA|SANTO NINO",
      "MARIKINA|CONCEPCION 2":       "MARIKINA|CONCEPCION DOS",
      "MARIKINA|MARIKINA HEIGHTS":   "MARIKINA|MARIKINA HEIGHTS",
      "MARIKINA|JESUS DELA PENA":    "MARIKINA|JESUS DE LA PENA",
      "MARIKINA|TANONG":             "MARIKINA|TANONG",
      "MARIKINA|INDUSTRIAL VALLEY":  "MARIKINA|INDUSTRIAL VALLEY",
      "MARIKINA|PARAISO":            "MARIKINA|FORTUNE",
      "PASIG|STA CRUZ":              "PASIG|SANTA CRUZ",
      "PASIG|STA LUCIA":             "PASIG|SANTA LUCIA",
      "PASIG|STO TOMAS":             "PASIG|SANTO TOMAS",
      "PASIG|SAN NICOLAS":           "PASIG|SAN NICOLAS",
      "PASIG|KATIPUNAN":             "PASIG|BAGONG KATIPUNAN",
      "PASIG|NAPICO":                "PASIG|PINAGBUHATAN",
      "PASIG|UGONG NORTE":           "PASIG|UGONG",
      "MANDALUYONG|NEW ZANIGA":      "MANDALUYONG|NEW ZANIGA",
      "MANDALUYONG|OLD ZANIGA":      "MANDALUYONG|OLD ZANIGA",
      "MANDALUYONG|PAG ASA":         "MANDALUYONG|PAG ASA",
      "MANDALUYONG|MABINI J RIZAL":  "MANDALUYONG|MABINI J RIZAL",
      "MANDALUYONG|WACK WACK":       "MANDALUYONG|WACK WACK GREENHILLS",
      "MANDALUYONG|WACK WACK GREENHILLS": "MANDALUYONG|WACK WACK GREENHILLS",
      "MANDALUYONG|HAGDANG BATO":    "MANDALUYONG|HAGDANG BATO ITAAS",
      "MANDALUYONG|GREENHILLS":      "MANDALUYONG|WACK WACK GREENHILLS",
      "SAN JUAN|STA LUCIA":          "SAN JUAN|SANTA LUCIA",
      "SAN JUAN|BALONG BATO":        "SAN JUAN|BALONG BATO",
      "SAN JUAN|ERMITANO":           "SAN JUAN|ERMITANO",
      "SAN JUAN|PASADENA":           "SAN JUAN|PASADENA",
      "SAN JUAN|SAN JOSE":           "SAN JUAN|SAN PERFECTO",
    };

    for (const [dbKey, geoKey] of Object.entries(ALIASES)) {
      // Find the GeoJSON entry by normalized key
      const normGeo = norm(geoKey.split("|")[1]);
      const muni = geoKey.split("|")[0];
      const resolved = lookup[`${muni}|${normGeo}`] || lookup[geoKey];
      if (resolved) lookup[dbKey] = resolved;
    }

    return lookup;
  }, [brgyGeoJSON]);

  // Resolve coords: prefer GeoJSON centroid (exact or normalized), fall back to static data
  const resolveCoords = useCallback(
    (municipality, barangay) => {
      const muni = (municipality || "").toUpperCase();
      const brgy = (barangay || "").toUpperCase();
      const key = `${muni}|${brgy}`;
      if (geoCentroids[key]) return geoCentroids[key];
      // Try normalized
      const normKey = `${muni}|${brgy.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\(.*?\)/g, "").replace(/[.\-]/g, " ").replace(/\s+/g, " ").trim()}`;
      if (geoCentroids[normKey]) return geoCentroids[normKey];
      return getBarangayCoords(municipality, barangay);
    },
    [geoCentroids]
  );

  // Open barangay records modal on double-click (respects active filters)
  const openBrgyRecords = useCallback(async (municipality, barangay) => {
    setBrgyModal({ municipality, barangay });
    setBrgyRecordsLoading(true);
    setBrgySelected(new Set());
    setActionMsg("");
    try {
      const params = { municipality, barangay };
      if (filterBundle) params.bundle_type = filterBundle;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get("/migration/dashboard/barangay-records", { params });
      setBrgyRecords(res.data);
    } catch {
      setBrgyRecords([]);
    } finally {
      setBrgyRecordsLoading(false);
    }
  }, [filterBundle, filterStatus]);

  // Load boundary GeoJSON files
  useEffect(() => {
    fetch("/data/barangay_boundaries.geojson")
      .then((r) => r.json())
      .then(setBrgyGeoJSON)
      .catch(() => {});
    fetch("/data/city_boundaries.geojson")
      .then((r) => r.json())
      .then(setCityGeoJSON)
      .catch(() => {});
  }, []);

  // Redirect if no permission
  useEffect(() => {
    if (!canAccessMigrationDashboard) navigate("/dashboard", { replace: true });
  }, [canAccessMigrationDashboard, navigate]);

  // Fetch bundle types and decomm values once on mount
  useEffect(() => {
    api.get("/migration/dashboard/bundle-types")
      .then((r) => setBundleTypes(r.data))
      .catch(() => {});
    api.get("/migration/dashboard/decomm-values")
      .then((r) => setDecommValues(r.data))
      .catch(() => {});
  }, []);

  // Fetch stats + barangay data + municipalities
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filterMuni) params.municipality = filterMuni;
      if (filterBundle) params.bundle_type = filterBundle;
      if (filterDecomm) params.decomm = filterDecomm;
      const statsParams = {};
      if (filterMuni) statsParams.municipality = filterMuni;
      if (filterBundle) statsParams.bundle_type = filterBundle;
      if (filterDecomm) statsParams.decomm = filterDecomm;

      const [statsRes, brgyRes, muniRes, teamsRes, teamsSumRes] = await Promise.all([
        api.get("/migration/dashboard/stats", { params: statsParams }),
        api.get("/migration/dashboard/barangay-summary", { params }),
        api.get("/migration/dashboard/municipalities"),
        api.get("/migration/dashboard/dispatch-teams-today", { params }),
        api.get("/migration/dashboard/dispatch-teams-today-summary", { params }),
      ]);
      setStats(statsRes.data);
      setBrgyData(brgyRes.data);
      setMunicipalities(muniRes.data);
      setDispatchTeams(teamsRes.data);
      setTeamsSummary(teamsSumRes.data);
      // Reset selected team when filters change so stale selection doesn't persist
      setSelectedTeam(null);
    } catch {
      setError("Failed to load migration dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [filterMuni, filterBundle, filterDecomm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReopenConfirm = async (recordId) => {
    setConfirmReopenId(null);
    try {
      await api.post(`/migration/${recordId}/reopen`, {
        call_remarks: reopenCallRemarks.trim(),
      });
      setReopenCallRemarks("");
      setSelectedRecord(null);
      fetchData();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to reopen record.");
    }
  };

  // Bulk move handler (needs fetchData)
  const handleBulkMove = useCallback(async (targetStage) => {
    const ids = [...brgySelected];
    if (!ids.length) return;

    const needsDate = targetStage === "for_dispatch" || targetStage === "visited_called";
    if (needsDate && !bulkDateModal) {
      const labels = {
        for_dispatch: "For Dispatch",
        visited_called: "Completed/Visited/Called",
        for_calling_scheduling: "For Calling/Scheduling",
      };
      setBulkDateModal({ stage: targetStage, label: labels[targetStage] });
      return;
    }

    try {
      const payload = { record_ids: ids, target_stage: targetStage };
      if (bulkDateModal) {
        payload.dispatch_date = bulkDate;
        if (targetStage === "visited_called" && bulkJoStatus) {
          payload.jo_status = bulkJoStatus;
          if (bulkJoStatus === "Cancelled") {
            if (bulkCancelReasons.length > 0) payload.cancellation_reasons = bulkCancelReasons;
            if (bulkRemarks.trim()) payload.remarks = bulkRemarks.trim();
          }
        }
      }
      await api.post("/migration/bulk-tag", payload);
      setActionMsg(`Moved ${ids.length} record(s) successfully.`);
      setBrgySelected(new Set());
      setBulkDateModal(null);
      setBulkDate("");
      setBulkJoStatus("");
      setBulkCancelReasons([]);
      setBulkRemarks("");
      // Refresh records (with same filters)
      try {
        const refreshParams = { municipality: brgyModal.municipality, barangay: brgyModal.barangay };
        if (filterBundle) refreshParams.bundle_type = filterBundle;
        if (filterStatus) refreshParams.status = filterStatus;
        const res = await api.get("/migration/dashboard/barangay-records", { params: refreshParams });
        setBrgyRecords(res.data);
      } catch { /* refresh failed but move succeeded */ }
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setActionMsg(typeof detail === "string" ? detail : "Failed to move records.");
    }
  }, [brgySelected, bulkDateModal, bulkDate, bulkJoStatus, bulkCancelReasons, bulkRemarks, brgyModal, fetchData, filterBundle, filterStatus]);

  // Fetch barangays when municipality filter changes
  useEffect(() => {
    if (!filterMuni) {
      setBarangays([]);
      setFilterBrgy("");
      return;
    }
    api
      .get("/migration/dashboard/barangays", {
        params: { municipality: filterMuni },
      })
      .then((r) => setBarangays(r.data))
      .catch(() => setBarangays([]));
    setFilterBrgy("");
  }, [filterMuni]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/migration/dashboard/search", {
          params: { q: term },
        });
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // Select a search result
  const handleSelectResult = (rec) => {
    setSelectedRecord(rec);
    setRightTab("subscriber");
    setSearchResults([]);
    setSearchTerm(rec.oldacctno || "");
    const coords = resolveCoords(rec.municipality, rec.barangay);
    if (coords) {
      setFlyTarget([...coords]);
      setFlyZoom(16);
    }
  };

  // Build team color map for dispatch markers (include summary teams for full coverage)
  const teamColorMap = useMemo(() => {
    const map = {};
    const allTeams = new Set();
    dispatchTeams.forEach((loc) => loc.teams.forEach((t) => allTeams.add(t.team_number)));
    teamsSummary.forEach((t) => allTeams.add(t.team_number));
    [...allTeams].sort().forEach((team, i) => {
      map[team] = TEAM_COLORS[i % TEAM_COLORS.length];
    });
    return map;
  }, [dispatchTeams, teamsSummary]);

  // Filtered dispatch markers based on selected team
  const filteredDispatchTeams = useMemo(() => {
    if (!selectedTeam) return dispatchTeams;
    return dispatchTeams
      .map((loc) => ({
        ...loc,
        teams: loc.teams.filter((t) => t.team_number === selectedTeam),
      }))
      .filter((loc) => loc.teams.length > 0);
  }, [dispatchTeams, selectedTeam]);

  // Set of barangays where the selected team operates (for circle filtering)
  const selectedTeamBrgys = useMemo(() => {
    if (!selectedTeam) return null;
    const s = new Set();
    dispatchTeams.forEach((loc) => {
      if (loc.teams.some((t) => t.team_number === selectedTeam)) {
        s.add(`${(loc.municipality || "").toUpperCase()}|${(loc.barangay || "").toUpperCase()}`);
      }
    });
    return s;
  }, [selectedTeam, dispatchTeams]);

  // Filter barangay data for map
  const filteredBrgyData = useMemo(() => {
    let data = brgyData;
    if (filterBrgy) {
      data = data.filter(
        (b) => b.barangay?.toUpperCase() === filterBrgy.toUpperCase()
      );
    }
    if (filterStatus) {
      const key = filterStatus.toLowerCase().replace(/ /g, "_");
      data = data.filter((b) => (b[key] || 0) > 0);
    }
    if (selectedTeamBrgys) {
      data = data.filter((b) =>
        selectedTeamBrgys.has(
          `${(b.municipality || "").toUpperCase()}|${(b.barangay || "").toUpperCase()}`
        )
      );
    }
    return data;
  }, [brgyData, filterBrgy, filterStatus, selectedTeamBrgys]);

  // Top barangays by pending + not yet visited
  const topPendingBrgys = useMemo(() => {
    return [...brgyData]
      .map((b) => ({
        ...b,
        actionable: (b.pending || 0) + (b.not_yet_visited || 0),
      }))
      .filter((b) => b.actionable > 0)
      .sort((a, b) => b.actionable - a.actionable)
      .slice(0, 10);
  }, [brgyData]);

  // Card click handlers
  const goTo = (path) => navigate(path);

  if (!canAccessMigrationDashboard) return null;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      {/* Module accent strip */}
      {!isMobile && <div className={`h-1 ${MIG.strip} flex-shrink-0`} />}

      <main className="flex-1 px-4 lg:px-6 pt-3 pb-2 w-full">
        {/* Page title */}
        <div className="mb-4 flex items-center gap-3">
          <div className={`${MIG.badge} rounded-lg shadow-sm flex items-center gap-2 px-3 py-1.5`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={MIG.iconPath} /></svg>
            <span className="text-xs font-bold uppercase tracking-wider">Migration</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800">
            Migration Progress Dashboard
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-4">
            {error}
          </div>
        )}

        {/* ============================================================ */}
        {/* A. TOP CONTROL AREA                                          */}
        {/* ============================================================ */}
        <div className="bg-white rounded-lg shadow px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px] max-w-[360px]">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedRecord(null);
              }}
              placeholder="Search Old Acct No. or Name..."
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searching && (
              <div className="absolute right-3 top-2.5 text-xs text-gray-400">
                ...
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-64 overflow-y-auto">
                {searchResults.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleSelectResult(r)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0"
                  >
                    <span className="font-medium text-gray-800">
                      {r.oldacctno}
                    </span>
                    <span className="text-gray-500 ml-2">{r.name}</span>
                    <span className="text-gray-400 ml-2 text-xs">
                      {r.barangay}, {r.municipality}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Municipality filter */}
          <select
            value={filterMuni}
            onChange={(e) => setFilterMuni(e.target.value)}
            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Municipalities</option>
            {municipalities.map((m) => (
              <option key={m.name} value={m.name}>
                {m.name} ({m.count})
              </option>
            ))}
          </select>

          {/* Barangay filter */}
          <select
            value={filterBrgy}
            onChange={(e) => setFilterBrgy(e.target.value)}
            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!filterMuni}
          >
            <option value="">All Barangays</option>
            {barangays.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          {/* Bundle Type filter */}
          <select
            value={filterBundle}
            onChange={(e) => setFilterBundle(e.target.value)}
            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Bundle Types</option>
            {bundleTypes.map((bt) => (
              <option key={bt} value={bt}>
                {bt}
              </option>
            ))}
          </select>

          {/* Decomm filter */}
          <select
            value={filterDecomm}
            onChange={(e) => setFilterDecomm(e.target.value)}
            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Decomm?</option>
            {decommValues.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          {canTag && (
            <button
              onClick={() => setShowAddSubscriber(true)}
              className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium whitespace-nowrap"
            >
              + Add Subscriber
            </button>
          )}
        </div>

        {/* ============================================================ */}
        {/* B. STATISTICS SHORTCUT CARDS                                  */}
        {/* ============================================================ */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <StatCard
            label="Uploaded for Migration"
            count={stats?.uploaded_for_migration}
            color="bg-blue-500"
            onClick={() => goTo("/migration/uploaded")}
            loading={loading}
          />
          <StatCard
            label="For Calling/Scheduling"
            count={stats?.for_calling_scheduling}
            color="bg-indigo-500"
            onClick={() => goTo("/migration/for-calling")}
            loading={loading}
          />
          <StatCard
            label="For Dispatch Today"
            count={stats?.dispatch_today}
            color="bg-amber-500"
            onClick={() => goTo("/migration/for-dispatch")}
            loading={loading}
          />
          <StatCard
            label="Completed/Visited/Called"
            count={stats?.visited_called}
            color="bg-emerald-500"
            onClick={() => goTo("/migration/visited-called")}
            loading={loading}
          />
          <StatCard
            label="Total Unique Subscribers"
            count={stats?.total_subscribers}
            color="bg-gray-600"
            loading={loading}
          />
        </div>

        {/* ============================================================ */}
        {/* C. MAIN CONTENT: LEFT | CENTER MAP | RIGHT                    */}
        {/* ============================================================ */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_300px] gap-4">
          {/* ---------- LEFT PANEL ---------- */}
          <div className="space-y-4">
            {/* Top Barangays */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                Top Barangays (Pending / Not Visited)
              </h3>
              {topPendingBrgys.length === 0 ? (
                <p className="text-xs text-gray-400">No data</p>
              ) : (
                <div className="space-y-1.5">
                  {topPendingBrgys.map((b, i) => (
                    <button
                      key={`${b.municipality}-${b.barangay}`}
                      onClick={() => {
                        const coords = resolveCoords(
                          b.municipality,
                          b.barangay
                        );
                        if (coords) {
                          setFlyTarget([...coords]);
                          setFlyZoom(15);
                        }
                      }}
                      className="w-full flex items-center gap-2 text-xs hover:bg-gray-50 rounded px-1 py-1"
                    >
                      <span className="text-gray-400 w-4 text-right">
                        {i + 1}.
                      </span>
                      <span className="text-gray-700 truncate flex-1 text-left">
                        {b.barangay}
                      </span>
                      <span className="text-gray-400">{b.municipality}</span>
                      <span className="font-medium text-amber-600 w-8 text-right">
                        {b.actionable}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ---------- CENTER: MAP ---------- */}
          <div className="bg-white rounded-lg shadow overflow-hidden relative" style={{ minHeight: 500 }}>
            {/* Compact status legend overlay (lower-left) — parity with Migration Dispatch Map */}
            {!loading && (
              <div className="absolute bottom-3 left-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-md shadow-md border px-2 py-1.5 flex flex-col gap-0.5">
                {Object.entries(STATUS_COLORS).map(([label, color]) => {
                  const isActive = filterStatus === label;
                  return (
                    <button
                      key={label}
                      onClick={() => setFilterStatus(isActive ? "" : label)}
                      className={`flex items-center gap-1.5 text-[10.5px] rounded px-1 py-[2px] transition-colors ${
                        isActive ? "bg-gray-800 text-white font-semibold" : "hover:bg-gray-100 text-gray-700"
                      }`}
                      title={`Filter: ${label}`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-white"
                        style={{ backgroundColor: color }}
                      />
                      <span>{label}</span>
                      {stats?.status_distribution && (
                        <span className={`ml-auto tabular-nums text-[10px] ${isActive ? "text-gray-300" : "text-gray-400"}`}>
                          {stats.status_distribution[label] || 0}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                Loading map...
              </div>
            ) : (
              <MapContainer
                center={SERVICE_AREA_CENTER}
                zoom={SERVICE_AREA_ZOOM}
                maxZoom={21}
                style={{ height: "100%", minHeight: 500, background: "#ffffff" }}
                scrollWheelZoom
              >
                {showBasemap && (
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={21}
                  />
                )}
                {showSatellite && (
                  <TileLayer
                    attribution="&copy; Google"
                    url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                    maxZoom={21}
                  />
                )}
                {showStreetLabels && (
                  <TileLayer
                    key="road-labels"
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                    pane="overlayPane"
                    maxZoom={21}
                  />
                )}
                {showLandmarks && (
                  <TileLayer
                    key="landmarks"
                    attribution="&copy; Google"
                    url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
                    pane="overlayPane"
                    maxZoom={21}
                  />
                )}

                {flyTarget && <FlyTo center={flyTarget} zoom={flyZoom} />}
                <ZoomWatcher onZoomChange={(z) => { setShowLandmarks(z >= 17); setShowSatellite(z >= 20); }} />

                {/* City boundary layer (non-interactive so circles get events) */}}
                {showCityBoundaries && cityGeoJSON && (
                  <GeoJSON
                    key="city-boundaries"
                    data={cityGeoJSON}
                    interactive={false}
                    style={{
                      color: "#1e40af",
                      weight: 3,
                      fillColor: "#3b82f6",
                      fillOpacity: 0.04,
                      dashArray: "6 4",
                      interactive: false,
                    }}
                    onEachFeature={(feature, layer) => {
                      const name = feature.properties.city_name || "";
                      const shortName = name.replace(/^City of /i, "");
                      layer.bindTooltip(shortName, {
                        permanent: true,
                        direction: "center",
                        className: "city-label-tooltip",
                      });
                    }}
                  />
                )}

                {/* Barangay boundary layer (non-interactive so circles get events) */}
                {showBrgyBoundaries && brgyGeoJSON && (
                  <GeoJSON
                    key="brgy-boundaries"
                    data={brgyGeoJSON}
                    interactive={false}
                    style={{
                      color: "#6b7280",
                      weight: 1,
                      fillColor: "#d1d5db",
                      fillOpacity: 0.08,
                      interactive: false,
                    }}
                  />
                )}

                {/* Barangay name labels */}
                {showBrgyLabels && brgyGeoJSON && (
                  <BrgyLabels data={brgyGeoJSON} />
                )}

                {/* Head Office marker */}
                <Marker position={HEAD_OFFICE} icon={HEAD_OFFICE_ICON} pane="markerPane">
                  <Tooltip direction="top" offset={[0, -18]}>
                    <div className="text-xs font-bold">BEST Inc. Head Office</div>
                  </Tooltip>
                </Marker>

                {/* Barangay data circles */}
                {filteredBrgyData.map((b) => {
                  const coords = resolveCoords(b.municipality, b.barangay);
                  if (!coords) return null;
                  const size = circleRadius(b.total) * 2;

                  if (filterStatus) {
                    // Single-color circle when a status filter is active
                    const color = STATUS_COLORS[filterStatus] || "#9ca3af";
                    return (
                      <CircleMarker
                        key={`${b.municipality}-${b.barangay}`}
                        center={coords}
                        radius={circleRadius(b.total)}
                        pane="markerPane"
                        pathOptions={{
                          fillColor: color,
                          fillOpacity: 0.8,
                          color: "#fff",
                          weight: 1.5,
                        }}
                        eventHandlers={{
                          click: () => {
                            setSelectedRecord(null);
                            setFlyTarget([...coords]);
                            setFlyZoom(15);
                          },
                          dblclick: () => {
                            openBrgyRecords(b.municipality, b.barangay);
                          },
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -8]}>
                          <div className="text-xs">
                            <div className="font-bold">
                              {b.barangay}, {b.municipality}
                            </div>
                            <div>Total: {b.total}</div>
                            <div>{filterStatus}: {b[filterStatus.toLowerCase().replace(/ /g, "_")] || 0}</div>
                          </div>
                        </Tooltip>
                      </CircleMarker>
                    );
                  }

                  // Pie-chart marker when no filter is active
                  return (
                    <Marker
                      key={`${b.municipality}-${b.barangay}`}
                      position={coords}
                      icon={buildPieIcon(b, size)}
                      pane="markerPane"
                      eventHandlers={{
                        click: () => {
                          setSelectedRecord(null);
                          setFlyTarget([...coords]);
                          setFlyZoom(15);
                        },
                        dblclick: () => {
                          openBrgyRecords(b.municipality, b.barangay);
                        },
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -8]}>
                        <div className="text-xs">
                          <div className="font-bold">
                            {b.barangay}, {b.municipality}
                          </div>
                          <div>Total: {b.total}</div>
                          {b.completed > 0 && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full" style={{background: STATUS_COLORS.Completed}}/>
                              Completed: {b.completed}
                            </div>
                          )}
                          {b.cancelled > 0 && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full" style={{background: STATUS_COLORS.Cancelled}}/>
                              Cancelled: {b.cancelled}
                            </div>
                          )}
                          {b.rescheduled > 0 && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full" style={{background: STATUS_COLORS.Rescheduled}}/>
                              Rescheduled: {b.rescheduled}
                            </div>
                          )}
                          {b.pending > 0 && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full" style={{background: STATUS_COLORS.Pending}}/>
                              Pending: {b.pending}
                            </div>
                          )}
                          {b.not_yet_visited > 0 && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full" style={{background: STATUS_COLORS["Not Yet Visited"]}}/>
                              Not Yet Visited: {b.not_yet_visited}
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                })}

                {/* Selected subscriber marker */}
                {selectedRecord && (() => {
                  const coords = resolveCoords(
                    selectedRecord.municipality,
                    selectedRecord.barangay
                  );
                  if (!coords) return null;
                  return (
                    <CircleMarker
                      center={coords}
                      radius={8}
                      pathOptions={{
                        fillColor: "#3b82f6",
                        fillOpacity: 1,
                        color: "#1e40af",
                        weight: 3,
                      }}
                    >
                      <Popup>
                        <div className="text-xs">
                          <div className="font-bold">
                            {selectedRecord.oldacctno}
                          </div>
                          <div>{selectedRecord.name}</div>
                          <div>
                            {selectedRecord.barangay},{" "}
                            {selectedRecord.municipality}
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })()}
              </MapContainer>
            )}
          </div>

          {/* ---------- RIGHT PANEL ---------- */}
          <div className="flex flex-col">
            {/* Tab headers */}
            <div className="flex border-b border-gray-200 bg-white rounded-t-lg shadow-sm flex-shrink-0">
              {[
                { key: "layers", label: "Layers" },
                { key: "subscriber", label: "Subscriber" },
                { key: "municipality", label: "Municipality" },
              ].map((t) => {
                const active = rightTab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setRightTab(t.key)}
                    className={`flex-1 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide transition-colors border-b-2 ${
                      active
                        ? "text-purple-600 border-purple-600 bg-purple-50/40"
                        : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Tab body */}
            <div className="bg-white rounded-b-lg shadow flex-1 p-4">
              {/* Tab: Layers */}
              {rightTab === "layers" && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Layers</div>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showBasemap}
                      onChange={(e) => setShowBasemap(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    Street Map
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showSatellite}
                      onChange={(e) => setShowSatellite(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    Satellite
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showStreetLabels}
                      onChange={(e) => setShowStreetLabels(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    Street / Road Names
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showLandmarks}
                      onChange={(e) => setShowLandmarks(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    Landmarks
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showCityBoundaries}
                      onChange={(e) => setShowCityBoundaries(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    City Boundaries
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showBrgyBoundaries}
                      onChange={(e) => setShowBrgyBoundaries(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    Barangay Boundaries
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showBrgyLabels}
                      onChange={(e) => setShowBrgyLabels(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    Barangay Names
                  </label>
                </div>
              )}

              {/* Tab: Subscriber Detail */}
              {rightTab === "subscriber" && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Subscriber Detail
                  </h3>
                  {selectedRecord ? (
                    <SubscriberDetail
                      record={selectedRecord}
                      canTag={canTag}
                      onMoveDirectly={async (targetStage, ids) => {
                        try {
                          await api.post("/migration/bulk-tag", {
                            record_ids: ids,
                            target_stage: targetStage,
                          });
                          setSelectedRecord(null);
                          fetchData();
                        } catch (err) {
                          alert(err?.response?.data?.detail || "Failed to move record.");
                        }
                      }}
                      onMoveWithDate={(targetStage, ids) => {
                        setBrgySelected(new Set(ids));
                        setBrgyModal({ municipality: selectedRecord.municipality, barangay: selectedRecord.barangay });
                        const labels = {
                          for_dispatch: "For Dispatch",
                          visited_called: "Completed/Visited/Called",
                          for_calling_scheduling: "For Calling/Scheduling",
                        };
                        setBulkDateModal({ stage: targetStage, label: labels[targetStage] });
                      }}
                      canReopen={canReopenMig}
                      onReopen={() => setConfirmReopenId(selectedRecord.id)}
                    />
                  ) : (
                    <p className="text-xs text-gray-400">
                      Search for a subscriber or click a barangay on the map to view
                      details.
                    </p>
                  )}
                </div>
              )}

              {/* Tab: Municipality Summary */}
              {rightTab === "municipality" && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    Municipality Summary
                  </h3>
                  {stats ? (
                    <div className="space-y-2">
                      {municipalities
                        .filter((m) =>
                          ["PASIG", "MANDALUYONG", "MARIKINA", "SAN JUAN"].includes(
                            m.name?.toUpperCase()
                          )
                        )
                        .sort((a, b) => b.count - a.count)
                        .map((m) => (
                          <button
                            key={m.name}
                            onClick={() => {
                              setFilterMuni(m.name);
                              const coords =
                                CITY_CENTERS[m.name?.toUpperCase()];
                              if (coords) {
                                setFlyTarget([...coords]);
                                setFlyZoom(14);
                              }
                            }}
                            className="w-full flex items-center justify-between text-sm hover:bg-gray-50 rounded px-2 py-1"
                          >
                            <span className="text-gray-700">{m.name}</span>
                            <span className="font-medium text-gray-500">
                              {m.count}
                            </span>
                          </button>
                        ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ============================================================ */}
      {/* BARANGAY RECORDS MODAL                                        */}
      {/* ============================================================ */}
      {brgyModal && (
        <BarangayRecordsModal
          municipality={brgyModal.municipality}
          barangay={brgyModal.barangay}
          records={brgyRecords}
          loading={brgyRecordsLoading}
          selected={brgySelected}
          setSelected={setBrgySelected}
          canTag={canTag}
          actionMsg={actionMsg}
          onMove={handleBulkMove}
          onClose={() => {
            setBrgyModal(null);
            setBrgyRecords([]);
            setBrgySelected(new Set());
            setActionMsg("");
            setBulkDateModal(null);
          }}
        />
      )}

      {/* Add Subscriber Modal */}
      {confirmReopenId && (
        <div className="fixed inset-0 bg-black/40 z-[2001] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold mb-2">Reopen Migration Record</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to reopen this cancelled migration record? The status will be changed back to <span className="font-semibold text-blue-700">Pending</span> and the dispatch date will be set to today.
            </p>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Call Status</label>
              <input type="text" value="Confirmed" disabled className="w-full border rounded px-3 py-2 text-sm bg-gray-100 text-gray-600" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Call Remarks <span className="text-red-500">*</span></label>
              <textarea
                value={reopenCallRemarks}
                onChange={(e) => setReopenCallRemarks(e.target.value)}
                placeholder="Enter call remarks"
                rows={3}
                className="w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
              />
              {reopenCallRemarks.length > 0 && !reopenCallRemarks.trim() && (
                <p className="text-xs text-red-500 mt-1">Call remarks cannot be blank.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setConfirmReopenId(null); setReopenCallRemarks(""); }} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={() => handleReopenConfirm(confirmReopenId)} disabled={!reopenCallRemarks.trim()} className="px-4 py-2 text-sm text-white rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed">Confirm Reopen</button>
            </div>
          </div>
        </div>
      )}

      {showAddSubscriber && (
        <AddSubscriberModal
          municipalities={municipalities}
          bundleTypes={bundleTypes}
          onClose={() => setShowAddSubscriber(false)}
          onSuccess={() => {
            setShowAddSubscriber(false);
            fetchData();
          }}
        />
      )}

      {/* Date / JO Status modal for bulk move */}
      {bulkDateModal && (
        <div className="fixed inset-0 bg-black/40 z-[2001] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold text-gray-800">
              Move to {bulkDateModal.label}
            </h3>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Dispatch Date</label>
              <input
                type="date"
                value={bulkDate}
                onChange={(e) => setBulkDate(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            {bulkDateModal.stage === "visited_called" && (
              <>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">JO Status</label>
                  <select
                    value={bulkJoStatus}
                    onChange={(e) => { setBulkJoStatus(e.target.value); if (e.target.value !== "Cancelled") { setBulkCancelReasons([]); setBulkRemarks(""); } }}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select Status</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                {bulkJoStatus === "Cancelled" && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Cancellation Reasons</label>
                      <div className="space-y-2">
                        {["Cancelled by Subs", "Needs RTA", "Full NAP", "No Converge Facility", "For NI", "Other reason"].map((reason) => (
                          <label key={reason} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={bulkCancelReasons.includes(reason)}
                              onChange={() => setBulkCancelReasons((prev) =>
                                prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
                              )}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            {reason}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Remarks</label>
                      <textarea
                        value={bulkRemarks}
                        onChange={(e) => setBulkRemarks(e.target.value)}
                        placeholder="Enter remarks..."
                        rows={2}
                        className="w-full border rounded px-3 py-2 text-sm resize-none"
                      />
                    </div>
                    {bulkCancelReasons.length > 0 && bulkRemarks.trim() && (
                      <p className="text-xs text-gray-500">
                        Will save as: <span className="font-medium">{bulkCancelReasons.join("; ")} - {bulkRemarks.trim()}</span>
                      </p>
                    )}
                  </>
                )}
              </>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setBulkDateModal(null); setBulkDate(""); setBulkJoStatus(""); setBulkCancelReasons([]); setBulkRemarks(""); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => handleBulkMove(bulkDateModal.stage)}
                disabled={
                  !bulkDate
                  || (bulkDateModal.stage === "visited_called" && !bulkJoStatus)
                  || (bulkJoStatus === "Cancelled" && bulkCancelReasons.length === 0)
                  || (bulkJoStatus === "Cancelled" && !bulkRemarks.trim())
                }
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Subscriber Modal
// ---------------------------------------------------------------------------
const SERVICE_MUNICIPALITIES = ["PASIG", "MANDALUYONG", "MARIKINA", "SAN JUAN"];

function AddSubscriberModal({ municipalities, bundleTypes, onClose, onSuccess }) {
  const [form, setForm] = useState({
    oldacctno: "",
    name: "",
    address: "",
    municipalityname: "",
    brgy: "",
    bundletype: "",
    contact_info: "",
    village: "",
    sdu_mdu: "",
    oldnode: "",
    typeofcustomer: "",
    subscriberstatusname: "",
    networktype: "",
    servicetype_based_on_tech: "",
    deployment_type: "",
    playtype: "",
  });
  const [barangays, setBarangays] = useState([]);
  const [dispatchDate, setDispatchDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showDispatchConfirm, setShowDispatchConfirm] = useState(false);
  const [dispatchCallRemarks, setDispatchCallRemarks] = useState("");

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Populate barangays from reference list when municipality changes
  useEffect(() => {
    if (!form.municipalityname) { setBarangays([]); return; }
    setBarangays(REFERENCE_BARANGAYS[form.municipalityname] || []);
  }, [form.municipalityname]);

  const isValid = form.oldacctno.trim() && form.name.trim() && form.municipalityname && form.brgy && form.address.trim() && form.contact_info.trim() && form.bundletype;

  const handleSubmit = async (targetStage, extras = {}) => {
    if (!isValid) return;
    if (targetStage === "for_dispatch" && !dispatchDate) {
      setError("Dispatch date is required for For Dispatch.");
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const payload = { ...form, target_stage: targetStage, ...extras };
      if (targetStage === "for_dispatch") payload.dispatch_date = dispatchDate;
      await api.post("/migration/dashboard/add-subscriber", payload);
      setSuccess(`Subscriber ${form.oldacctno} added to ${WORKFLOW_LABELS[targetStage]}.`);
      setTimeout(() => onSuccess(), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to add subscriber.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDispatchClick = () => {
    if (!isValid) return;
    if (!dispatchDate) {
      setError("Dispatch date is required for For Dispatch.");
      return;
    }
    setError("");
    setShowDispatchConfirm(true);
  };

  const handleDispatchConfirm = () => {
    setShowDispatchConfirm(false);
    handleSubmit("for_dispatch", {
      call_status: "Confirmed",
      call_remarks: dispatchCallRemarks.trim(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">Add Subscriber</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded">{error}</div>}
          {success && <div className="bg-green-50 text-green-700 text-sm px-3 py-2 rounded">{success}</div>}

          {/* Required fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Old Account No. <span className="text-red-500">*</span></label>
              <input value={form.oldacctno} onChange={set("oldacctno")} className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g. 679010301" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
              <input value={form.name} onChange={set("name")} className="w-full border rounded px-3 py-2 text-sm" placeholder="LAST, FIRST M" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Municipality <span className="text-red-500">*</span></label>
              <select value={form.municipalityname} onChange={set("municipalityname")} className="w-full border rounded px-3 py-2 text-sm">
                <option value="">Select Municipality</option>
                {SERVICE_MUNICIPALITIES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Barangay <span className="text-red-500">*</span></label>
              <select value={form.brgy} onChange={set("brgy")} className="w-full border rounded px-3 py-2 text-sm" disabled={!form.municipalityname}>
                <option value="">Select Barangay</option>
                {barangays.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Address <span className="text-red-500">*</span></label>
            <input value={form.address} onChange={set("address")} className="w-full border rounded px-3 py-2 text-sm" placeholder="Full address" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contact Info <span className="text-red-500">*</span></label>
              <input value={form.contact_info} onChange={set("contact_info")} className="w-full border rounded px-3 py-2 text-sm" placeholder="Phone number(s)" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bundle Type <span className="text-red-500">*</span></label>
              <select value={form.bundletype} onChange={set("bundletype")} className="w-full border rounded px-3 py-2 text-sm">
                <option value="">Select</option>
                {bundleTypes.map((bt) => (
                  <option key={bt} value={bt}>{bt}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Village</label>
              <input value={form.village} onChange={set("village")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">SDU/MDU</label>
              <input value={form.sdu_mdu} onChange={set("sdu_mdu")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Old Node</label>
              <input value={form.oldnode} onChange={set("oldnode")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Customer Type</label>
              <input value={form.typeofcustomer} onChange={set("typeofcustomer")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Subscriber Status</label>
              <input value={form.subscriberstatusname} onChange={set("subscriberstatusname")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Network Type</label>
              <input value={form.networktype} onChange={set("networktype")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Service Type</label>
              <input value={form.servicetype_based_on_tech} onChange={set("servicetype_based_on_tech")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Deployment Type</label>
              <input value={form.deployment_type} onChange={set("deployment_type")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Play Type</label>
              <input value={form.playtype} onChange={set("playtype")} className="w-full border rounded px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Dispatch date (shown for For Dispatch action) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dispatch Date <span className="text-gray-400">(required for For Dispatch)</span></label>
            <input type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => handleSubmit("uploaded_for_migration")}
            disabled={!isValid || submitting}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50"
          >
            Add to Uploaded for Migration
          </button>
          <button
            onClick={() => handleSubmit("for_calling_scheduling")}
            disabled={!isValid || submitting}
            className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded disabled:opacity-50"
          >
            Add to For Calling/Scheduling
          </button>
          <button
            onClick={handleDispatchClick}
            disabled={!isValid || !dispatchDate || submitting}
            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-50"
          >
            Add to For Dispatch
          </button>
          <button
            onClick={onClose}
            className="ml-auto px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded"
          >
            Cancel
          </button>
        </div>

        {showDispatchConfirm && (
          <div className="absolute inset-0 bg-black/40 z-10 flex items-center justify-center p-4 rounded-lg">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Add to For Dispatch</h3>
              <p className="text-sm text-gray-600 mb-4">
                This subscriber will be added directly to <span className="font-semibold text-blue-700">For Dispatch</span> with status <span className="font-semibold text-blue-700">Pending</span>.
              </p>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Call Status</label>
                <input type="text" value="Confirmed" disabled className="w-full border rounded px-3 py-2 text-sm bg-gray-100 text-gray-600" />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Call Remarks <span className="text-red-500">*</span></label>
                <textarea
                  value={dispatchCallRemarks}
                  onChange={(e) => setDispatchCallRemarks(e.target.value)}
                  placeholder="Enter call remarks"
                  rows={3}
                  className="w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
                />
                {dispatchCallRemarks.length > 0 && !dispatchCallRemarks.trim() && (
                  <p className="text-xs text-red-500 mt-1">Call remarks cannot be blank.</p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setShowDispatchConfirm(false); setDispatchCallRemarks(""); }} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
                <button type="button" onClick={handleDispatchConfirm} disabled={!dispatchCallRemarks.trim()} className="px-4 py-2 text-sm text-white rounded bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">Confirm & Add to For Dispatch</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Barangay Records Modal
// ---------------------------------------------------------------------------
function BarangayRecordsModal({
  municipality,
  barangay,
  records,
  loading,
  selected,
  setSelected,
  canTag,
  actionMsg,
  onMove,
  onClose,
}) {
  const allIds = records.map((r) => r.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const TAG_TARGETS = [
    { stage: "for_dispatch", label: "For Dispatch", color: "bg-indigo-600 hover:bg-indigo-700" },
    { stage: "visited_called", label: "Completed/Visited/Called", color: "bg-teal-600 hover:bg-teal-700" },
    { stage: "for_calling_scheduling", label: "For Calling/Scheduling", color: "bg-orange-500 hover:bg-orange-600" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {barangay}, {municipality}
            </h2>
            <p className="text-sm text-gray-500">
              {records.length} record(s) — excludes Completed/Visited/Called
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Action buttons */}
        {canTag && selected.size > 0 && (
          <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 border-b flex-wrap">
            {TAG_TARGETS.map((t) => (
              <button
                key={t.stage}
                onClick={() => onMove(t.stage)}
                className={`px-3 py-1.5 text-sm text-white rounded ${t.color}`}
              >
                Move to {t.label} ({selected.size})
              </button>
            ))}
          </div>
        )}

        {/* Status message */}
        {actionMsg && (
          <div className={`px-6 py-2 text-sm ${actionMsg.includes("Failed") ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
            {actionMsg}
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-gray-400">
              Loading records...
            </div>
          ) : records.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-400">
              No records found for this barangay.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {canTag && (
                    <th className="px-3 py-3 w-10 text-center">
                      <div
                        onClick={toggleSelectAll}
                        className={`w-4 h-4 mx-auto rounded border cursor-pointer flex items-center justify-center ${
                          allSelected
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "border-gray-300"
                        }`}
                      >
                        {allSelected && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </th>
                  )}
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">#</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Old Acct No.</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Name</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Bundle Type</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Stage</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">JO Status</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Team</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Personnel</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Dispatch Date</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-600">Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((r, i) => (
                  <tr
                    key={r.id}
                    className={`hover:bg-gray-50 ${selected.has(r.id) ? "bg-blue-50" : ""}`}
                  >
                    {canTag && (
                      <td className="px-3 py-2 text-center">
                        <div
                          onClick={() => toggleSelect(r.id)}
                          className={`w-4 h-4 mx-auto rounded border cursor-pointer flex items-center justify-center ${
                            selected.has(r.id)
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "border-gray-300"
                          }`}
                        >
                          {selected.has(r.id) && (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.oldacctno}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2">{r.bundle_type}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        (r.action_result && _TERMINAL_ACTIONS.includes(r.action_result)) ? "bg-green-100 text-green-700"
                        : r.workflow_stage === "uploaded_for_migration" ? "bg-purple-100 text-purple-700"
                        : r.workflow_stage === "for_calling_scheduling" ? "bg-orange-100 text-orange-700"
                        : r.workflow_stage === "for_dispatch" ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-700"
                      }`}>
                        {resolveWorkflowLabel(r)}
                      </span>
                    </td>
                    <td className="px-3 py-2">{r.jo_status || "—"}</td>
                    <td className="px-3 py-2">{r.team_number || "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.personnel || "—"}</td>
                    <td className="px-3 py-2">{r.dispatch_date || "—"}</td>
                    <td className="px-3 py-2 text-xs max-w-[200px] truncate">{r.address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({ label, count, color, onClick, loading }) {
  const inner = (
    <div
      className={`rounded-lg shadow p-4 text-white ${color} ${
        onClick ? "cursor-pointer hover:opacity-90 transition-opacity" : ""
      }`}
      onClick={onClick}
    >
      <div className="text-2xl font-bold">
        {loading ? "..." : (count ?? 0).toLocaleString()}
      </div>
      <div className="text-xs mt-1 opacity-90">{label}</div>
    </div>
  );
  return inner;
}

function SubscriberDetail({ record, canTag, onMoveDirectly, onMoveWithDate, canReopen, onReopen }) {
  const status = effectiveStatus(record);
  const pageLabel = resolveWorkflowLabel(record);

  // Determine which move buttons to show based on current stage
  const stage = record.workflow_stage;
  const showDispatch = stage === "uploaded_for_migration" || stage === "for_calling_scheduling";
  const showCalling = stage === "uploaded_for_migration";
  const showCompleted = stage === "uploaded_for_migration" || stage === "for_calling_scheduling";

  return (
    <div className="space-y-2 text-sm">
      <DetailRow label="Old Acct No." value={record.oldacctno} bold />
      <DetailRow label="Name" value={record.name} />
      <DetailRow label="Address" value={record.address} />
      <DetailRow label="Municipality" value={record.municipality} />
      <DetailRow label="Barangay" value={record.barangay} />
      <DetailRow label="Bundle Type" value={record.bundle_type} />
      <DetailRow
        label="Status"
        value={
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: STATUS_COLORS[status] || "#9ca3af" }}
            />
            {record.jo_status || "Not Yet Visited"}
          </span>
        }
      />
      {record.jo_status === "Cancelled" && canReopen && (
        <button
          onClick={onReopen}
          className="mt-1 px-3 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600"
        >
          Reopen
        </button>
      )}
      <DetailRow label="Workflow Page" value={pageLabel} />
      <DetailRow label="Team Number" value={record.team_number} />
      <DetailRow label="Dispatch Date" value={record.dispatch_date} />
      <DetailRow label="Call Status" value={record.call_status} />
      {record.remarks && <DetailRow label="Remarks" value={record.remarks} />}
      {record.contact_info && (
        <DetailRow label="Contact" value={record.contact_info} />
      )}

      {canTag && (showDispatch || showCalling || showCompleted) && (
        <div className="mt-3 space-y-1.5">
          {showDispatch && (
            <button
              onClick={() => onMoveWithDate("for_dispatch", [record.id])}
              className="w-full text-center text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded"
            >
              Add to For Dispatch
            </button>
          )}
          {showCalling && (
            <button
              onClick={() => onMoveDirectly("for_calling_scheduling", [record.id])}
              className="w-full text-center text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded"
            >
              Add to For Calling/Scheduling
            </button>
          )}
          {showCompleted && (
            <button
              onClick={() => onMoveWithDate("visited_called", [record.id])}
              className="w-full text-center text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded"
            >
              Add to Completed/Visited/Called
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, bold }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <span className="text-gray-400 text-xs">{label}</span>
      <div className={`text-gray-800 ${bold ? "font-semibold" : ""} break-words`}>
        {value}
      </div>
    </div>
  );
}
